import { useLanguage } from "@/i18n/LanguageContext";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown } from "lucide-react";
import { track } from "@/lib/analytics";
import { BeedigitInteractiveDashboard } from "@/components/beedigit-hero/BeedigitInteractiveDashboard";

const badges = [
  "Plug & Play hardware",
  "ERP / APS ready",
  "Industrial tablets & printers",
  "OEE · MES · Quality · CMMS · Energy",
  "Built for small factories & enterprise groups",
];

const leanBadges = [
  "Lean-ready",
  "Kaizen data foundation",
  "TPM support",
  "Six Sigma analytics",
  "Real-time OEE",
  "Trusted shop-floor data",
  "Daily Management System",
  "Visual factory control",
];

const Hero = () => {
  const { t } = useLanguage();
  return (
    <section className="relative overflow-hidden hero-wash">
      <div className="absolute inset-0 grid-lines opacity-[0.15] pointer-events-none" />
      <div className="container relative z-10 mx-auto px-4 pt-24 pb-16 md:pt-32 md:pb-24">
        <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-10 items-center">
          <div className="animate-fade-in">
            <p className="eyebrow mb-4">{t("BEEDIGIT · Plug & Play MES / OEE / IIoT")}</p>
            <h1 className="text-4xl md:text-5xl lg:text-[3.5rem] font-extrabold leading-[1.05] mb-5">
              {t("Increase factory output with")} <span className="text-primary">{t("Plug & Play MES")}</span>{t(", OEE and IIoT.")}
            </h1>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mb-6 leading-relaxed">
              {t("BEEDIGIT connects machines, operators, orders, materials, quality, maintenance and energy in one industrial platform. Start with one line, scale to full MES across your factory or group.")}
            </p>
            <p className="text-sm md:text-base font-semibold text-foreground max-w-2xl mb-6 leading-relaxed border-l-2 border-primary pl-3">
              {t("Built for factory Owners and Leaders who want more output, higher profit, and full production control based on real-time, trusted data — not delayed reports and assumptions.")}
            </p>

            <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm text-foreground/85 max-w-xl mb-7">
              {[
                "Preconfigured industrial hardware",
                "SaaS platform ready to use",
                "Industry-specific templates",
                "ERP and APS integration",
                "Old and new machines supported",
                "Fast rollout with minimum CAPEX",
              ].map((b) => (
                <li key={b} className="flex items-start gap-2">
                  <span className="mt-1.5 inline-block h-1.5 w-1.5 rounded-full bg-primary shrink-0" />{t(b)}
                </li>
              ))}
            </ul>

            <div className="flex flex-wrap gap-3 mb-6">
              <Button size="lg" className="shadow-glow-primary" asChild>
                <Link to="/get-started" onClick={() => track("cta_book_assessment_click", { location: "hero" })}>
                  {t("Book Factory Visibility Assessment")} <ArrowRight className="ms-2 h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link to="/pricing" onClick={() => track("cta_start_one_line_click", { location: "hero" })}>
                  {t("Start with One Bottleneck")} <ChevronDown className="ms-2 h-4 w-4" />
                </Link>
              </Button>
            </div>

            <div className="flex flex-wrap gap-2">
              {badges.map((b) => (
                <span key={b} className="pill pill-dot">{t(b)}</span>
              ))}
            </div>

            <div className="mt-4 flex flex-wrap gap-1.5">
              {leanBadges.map((b) => (
                <span key={b} className="text-[11px] px-2 py-1 rounded-md border border-primary/30 bg-primary/5 text-foreground/80">{t(b)}</span>
              ))}
            </div>
          </div>

          {/* Hero visual — interactive BEEDIGIT dashboard */}
          <div className="relative animate-fade-in w-full">
            <BeedigitInteractiveDashboard />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
